import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/firebase";
import { useAuthUser } from "@/hooks/useAuthUser";
import { useAuth } from "@/auth/AuthProvider";
import { Loader2 } from "lucide-react";
import { emitMetric } from "@/services/metricsService";
import { resolveAccess } from "@/lib/subscriptionStatus";
import type { UserSubscription } from "@/lib/subscriptionStatus";

interface RequireAuthProps {
  children?: ReactNode;
  redirectTo?: string;
}

function LoadingScreen({ message = "Loading…" }: { message?: string }) {
  return (
      <div className="grid min-h-screen place-items-center text-text">
      {/* Auth sentinel: Indicates auth check in progress for audit harness */}
      <div data-testid="app.auth.loading" className="hidden" aria-hidden="true" />
      <div className="flex items-center gap-2">
        <Loader2 className="animate-spin" size={20} />
        {message}
      </div>
    </div>
  );
}

export default function RequireAuth({ children, redirectTo = "/login" }: RequireAuthProps) {
  // CRITICAL: Call all hooks BEFORE any conditional returns (Rules of Hooks)
  const { status, firebaseUser, loading } = useAuthUser();
  const { claims } = useAuth(); // Get claims from AuthProvider
  const location = useLocation();
  const navigate = useNavigate();

  // Subscription access state — default true (optimistic) to avoid flash of paywall
  const [hasSubscriptionAccess, setHasSubscriptionAccess] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  // Subscription access gate: read users/{uid}.subscriptionStatus in real-time.
  // Fast path: claims.isPro / demo / auditBypassBilling → skip Firestore fetch.
  useEffect(() => {
    if (claims === undefined) return; // Still bootstrapping — wait for claims

    // Fast path: active subscriber or privileged account — no Firestore read needed
    // Admin accounts (super/support) are internal platform accounts and are never
    // subject to SRL subscription gating — bypass without touching Firestore.
    if (
      claims?.isPro === true ||
      claims?.demo === true ||
      claims?.auditBypassBilling === true ||
      claims?.admin === true ||
      claims?.role === 'super' ||
      claims?.role === 'support'
    ) {
      setHasSubscriptionAccess(true);
      setSubscriptionLoading(false);
      return;
    }

    if (!firebaseUser?.uid) {
      setSubscriptionLoading(false);
      return;
    }

    const uid = firebaseUser.uid;
    const ref = doc(db, 'users', uid);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          // New user doc not yet written — allow through (ensureUserDoc will write it shortly)
          setHasSubscriptionAccess(true);
          setSubscriptionLoading(false);
          return;
        }

        const data = snap.data() as Partial<UserSubscription> & Record<string, unknown>;

        // Legacy users without subscriptionStatus — check subscription.status (nested)
        // as a fallback before failing open. The nested field is written by billing.ts
        // syncBillingCustomer. If it carries a clearly-inactive status, deny access rather
        // than failing open — this closes the gap where stale subscription.status='active'
        // (written by the pre-billing-fix stripe.ts path) kept lapsed users in.
        // Only truly legacy users — those with NEITHER field set — continue to fail open.
        if (!data.subscriptionStatus) {
          const nestedStatus = (data as Record<string, unknown> & { subscription?: { status?: string } }).subscription?.status;
          const INACTIVE_NESTED = ['canceled', 'suspended', 'past_due', 'inactive'];
          if (nestedStatus && INACTIVE_NESTED.includes(nestedStatus)) {
            setHasSubscriptionAccess(false);
            setSubscriptionLoading(false);
            return;
          }
          // No top-level field and no clearly-inactive nested status — fail open
          // (covers true legacy users and users with subscription.status='active'/'trialing').
          setHasSubscriptionAccess(true);
          setSubscriptionLoading(false);
          return;
        }

        const { hasAccess } = resolveAccess({
          subscriptionStatus: data.subscriptionStatus as UserSubscription['subscriptionStatus'],
          trialStartedAt: data.trialStartedAt as UserSubscription['trialStartedAt'],
          trialEndsAt: data.trialEndsAt as UserSubscription['trialEndsAt'],
        });

        setHasSubscriptionAccess(hasAccess);
        setSubscriptionLoading(false);
      },
      () => {
        // On Firestore error, allow access to avoid locking users out
        setHasSubscriptionAccess(true);
        setSubscriptionLoading(false);
      }
    );

    return unsub;
  }, [firebaseUser?.uid, claims]);
  
  const isCypress = typeof window !== 'undefined' && (window as any).Cypress;
  const IS_DEV = import.meta.env.DEV;

  if (IS_DEV) console.log('[RequireAuth] COMPONENT INVOKED');
  if (IS_DEV) console.log('[RequireAuth] State:', { status, hasUser: !!firebaseUser, loading, pathname: location.pathname });
  const authObservedRef = useRef(false);
  const bootstrapWaitStartRef = useRef<number | null>(null);
  const BOOTSTRAP_TIMEOUT = 10000; // 10 seconds max wait for bootstrap

  // Check if we're in a "bootstrap pending" state
  // This happens when Firebase auth succeeded but claims haven't been fetched yet
  // claims will be undefined until getIdTokenResult() completes in AuthProvider
  const isBootstrapPending = 
    firebaseUser && 
    status === 'authed' && 
    claims === undefined; // Claims not loaded yet (vs loaded with falsy values)

  // Start bootstrap wait timer
  useEffect(() => {
    if (isBootstrapPending && bootstrapWaitStartRef.current === null) {
      bootstrapWaitStartRef.current = Date.now();
      console.log('[RequireAuth] Bootstrap pending detected, waiting for claims...');
    } else if (!isBootstrapPending) {
      bootstrapWaitStartRef.current = null;
    }
  }, [isBootstrapPending]);

  // Check if bootstrap has exceeded timeout
  const bootstrapTimedOut = 
    isBootstrapPending && 
    bootstrapWaitStartRef.current !== null &&
    Date.now() - bootstrapWaitStartRef.current > BOOTSTRAP_TIMEOUT;
  
  // Mark auth as "observed" once loading finishes at least once
  useEffect(() => {
    if (status !== "loading") {
      authObservedRef.current = true;
    }
  }, [status]);

  // [WO-GRAFANA-01] UID mismatch detection: fires when the authenticated UID
  // changes across a navigation event (e.g. token swap, session hi-jack).
  const prevUidRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const currentUid = firebaseUser?.uid ?? null;
    if (prevUidRef.current !== undefined && prevUidRef.current !== currentUid) {
      emitMetric('threadlock_uid_mismatch_total', 1, {
        uid_before: prevUidRef.current ?? 'null',
        uid_after:  currentUid ?? 'null',
      });
    }
    prevUidRef.current = currentUid;
  }, [location, firebaseUser]);  // re-check on every navigation
  
  // [P3 CANON] CYPRESS BYPASS: Allow initial render during bootstrap,
  // BUT redirect if user becomes null after we've observed auth.
  // ⚠️ WARNING: This bypass must never suppress post-load redirects when auth changes
  // (idle timeout, manual sign-out, token revocation, etc.).
  // Any bypass should only cover bootstrap wait, not ongoing auth state transitions.
  //
  // WHY `status` is in the dep array:
  // For an unauthenticated initial visit, `firebaseUser` is `null` in BOTH the
  // "loading" render AND the "unauthenticated" render — it never changes, so effect
  // deps would never trigger a re-run. Without `status` in deps, this effect fires
  // once on mount (when authObservedRef.current is still false → early return) and
  // never again. Adding `status` ensures the effect re-fires when auth resolves,
  // at which point effect #2 above has already set authObservedRef.current = true.
  useEffect(() => {
    if (!isCypress) return;
    if (!authObservedRef.current) return; // Still bootstrapping
    if (!firebaseUser) {
      // Post-load signout MUST redirect (validates idle timeout, manual logout, etc.)
      // Guard against infinite loop: don't redirect if already on redirectTo path
      if (location.pathname === redirectTo) return;
      
      const timestamp = new Date().toISOString();
      if (IS_DEV) console.log(`[T2-GUARD-CYPRESS] RequireAuth redirecting to login at ${timestamp}: user=null (post-bootstrap), path=${location.pathname}`);
      if (IS_DEV) console.warn('[RequireAuth] blocking', { uid: firebaseUser?.uid, path: location.pathname });
      navigate(redirectTo, { replace: true, state: { from: location } });
    }
  }, [isCypress, firebaseUser, status, redirectTo, location, navigate]);

  // Non-Cypress behavior remains strict
  if (!isCypress) {
    // Show loading during initial auth check
    if (status === "loading") {
      return <LoadingScreen />;
    }

    // Bootstrap pending: Show loading screen but don't redirect
    // This prevents redirect loops when bootstrap is slow
    if (isBootstrapPending && !bootstrapTimedOut) {
      console.log('[RequireAuth] Bootstrap pending, showing loading screen');
      return <LoadingScreen message="Completing sign in…" />;
    }

    // Bootstrap timed out: Proceed anyway (some features may be degraded)
    if (bootstrapTimedOut) {
      console.warn('[RequireAuth] Bootstrap timeout exceeded, proceeding with degraded state');
    }
    
    // No user and auth completed: redirect to login
    if (status === "unauthenticated" && !firebaseUser) {
      const timestamp = new Date().toISOString();
      if (IS_DEV) console.log(`[T2-GUARD] RequireAuth redirecting to login at ${timestamp}: status=${status}, user=null, path=${location.pathname}`);
      if (IS_DEV) console.warn('[RequireAuth] blocking', { uid: firebaseUser?.uid, path: location.pathname });
      return <Navigate to={redirectTo} replace />;
    }
  } else {
    // Cypress behavior: bypass during bootstrap, enforce after
    (window as any).__TL_AUTH_BYPASS__ = true;
    if (!authObservedRef.current) {
      // Bootstrap not complete - allow children (bypass)
      return children ? <>{children}</> : <Outlet />;
    }
    if (firebaseUser) {
      // Auth observed and user exists - allow children
      return children ? <>{children}</> : <Outlet />;
    }
    // Auth observed and user null - effect above will redirect, render nothing while redirecting
    return null;
  }

  // Phase 5 (P0): Enforce email verification for all protected routes
  // EXCEPTION: Demo accounts bypass email verification
  const isDemo = claims?.demo === true;
  
  // CRITICAL: Prevent infinite redirect loop by checking current location
  // If we're already on /verify-email, don't redirect again
  const isOnVerifyEmailPage = location.pathname === '/verify-email';
  
  if (!firebaseUser.emailVerified && !isDemo && !isOnVerifyEmailPage) {
    if (IS_DEV) console.warn('[RequireAuth] Email not verified, redirecting to /verify-email');
    if (IS_DEV) console.warn('[RequireAuth] blocking', { uid: firebaseUser?.uid, path: location.pathname });
    return <Navigate to="/verify-email" replace />;
  }

  // Subscription access gate (added after auth + email verification)
  // Show loading screen while checking subscription status for non-privileged users.
  // Exception: if bootstrap already timed out (claims never resolved), fail open and skip
  // this check — subscriptionLoading would never clear in a degraded-claims state.
  if (subscriptionLoading && !bootstrapTimedOut) {
    return <LoadingScreen message="Completing sign in…" />;
  }

  // If trial expired or subscription lapsed → redirect to paywall
  // (Bypass /subscribe itself to prevent a redirect loop — though /subscribe is a public route)
  if (!hasSubscriptionAccess && location.pathname !== '/subscribe') {
    return <Navigate to="/subscribe?reason=trial_expired" replace />;
  }

  if (children) {
    return <>{children}</>;
  }

  return <Outlet />;
}
